/**
 * Google Drive connector: status, the consent popup, and the Picker.
 */
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { normaliseSourcePath, type DriveImportFailure, type ProjectImport } from "@gb/contracts";
import { api, errorMessage } from "@/api/client";
import {
  documentKeys,
  importIsArriving,
  importKeys,
  useDocuments,
  useImport,
} from "./documents-api";

export const connectorKeys = {
  googleDrive: ["connectors", "google-drive"] as const,
};

export type ConnectorStatus = {
  connected: boolean;
  configured: boolean;
  accountEmail: string | null;
  pickerApiKey: string | null;
  clientId: string | null;
};

export function useGoogleDriveStatus() {
  return useQuery({
    queryKey: connectorKeys.googleDrive,
    queryFn: async (): Promise<ConnectorStatus> => {
      const res = await api.connectors.googleDriveStatus();
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useDisconnectGoogleDrive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.connectors.googleDriveDisconnect();
      if (res.status !== 204) throw new Error(errorMessage(res));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectorKeys.googleDrive });
    },
  });
}

/**
 * Run the consent flow in a popup and complete it.
 *
 * A popup rather than a full-page redirect: the wizard holds unsaved form state
 * on other steps, and navigating the whole tab away to Google and back would
 * discard it. The popup posts the code back to us and closes.
 */
export function useConnectGoogleDrive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      const urlRes = await api.connectors.googleDriveAuthUrl({ body: {} });
      if (urlRes.status !== 200) throw new Error(errorMessage(urlRes));

      const { code, state } = await runConsentPopup(urlRes.body.url);

      const callback = await api.connectors.googleDriveCallback({
        body: { code, state },
      });
      if (callback.status !== 200) throw new Error(errorMessage(callback));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectorKeys.googleDrive });
    },
  });
}

/** What a Drive pick answers: accepted now, fetched by the worker afterwards. */
export type DriveImportResult = {
  /** The import the files are in, as it stood when they were accepted. */
  import: ProjectImport;
  /** Files queued for fetching. */
  accepted: number;
  /** Files refused before any fetch was queued, such as a system file. */
  failed: DriveImportFailure[];
};

/**
 * Import files the user picked, into our own storage.
 *
 * Resolves as soon as the files are accepted, not when they have arrived. The
 * server answers 202 and the worker fetches each file afterwards
 * (`architecture/0028`), so the documents land over the following seconds.
 * `useDriveArrivals` follows them in.
 *
 * `importId` joins an import the screen already has open, as setup's documents
 * step does. Without it, the pick is an import of its own, opened and closed by
 * the server.
 *
 * Resolves with both halves of what is known now. A partial import is a
 * *success* at the transport level — the request worked, some files did not —
 * so it cannot be signalled by throwing; the caller has to render `failed` for
 * the user to learn that four of their six were taken.
 */
export function useImportFromDrive(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      files,
      importId,
    }: {
      files: PickedFile[];
      importId?: string;
    }): Promise<DriveImportResult> => {
      const res = await api.projects.importFromDrive({
        params: { id: projectId },
        body: { files, ...(importId ? { importId } : {}) },
      });
      if (res.status !== 202) throw new Error(errorMessage(res));
      return res.body;
    },
    onSuccess: (result) => {
      // The import as the response has it, so following it starts from there
      // rather than from nothing.
      queryClient.setQueryData(importKeys.detail(projectId, result.import.id), result.import);
      // The list, so a screen watching every import follows this one to its
      // end. Exact, or the prefix would re-read the import just set above.
      void queryClient.invalidateQueries({ queryKey: importKeys.list(projectId), exact: true });
      void queryClient.invalidateQueries({ queryKey: documentKeys.list(projectId) });
      // **And the Brain, which is a different key.** An imported file becomes a
      // `project_documents` row, and the Brain's list merges those with
      // knowledge entries under `brainKeys` — so invalidating only the
      // documents query left the file invisible until something else forced a
      // refetch. A local upload hid this: it renders its own in-flight row, so
      // it *looks* like it arrives. A Drive import has no such row and simply
      // did not appear.
      void queryClient.invalidateQueries({ queryKey: ["project", projectId, "brain"] });
    },
  });
}

/** The Drive import a screen is following in, and what it is waiting for. */
type Followed = {
  importId: string;
  /** Whether the picks joined an import the screen keeps open. */
  joined: boolean;
  /** Files accepted into it by this screen's picks. */
  expected: number;
  /**
   * Drive documents seen in it so far. Kept rather than counted from the list
   * each time, so a file deleted after it landed still counts as having landed.
   */
  landed: string[];
};

/**
 * Follow a Drive pick in until its files have landed (`architecture/0028`).
 *
 * The pick is accepted before any file is fetched. The files then appear over
 * the following seconds, and a file the worker could not fetch is reported on
 * the import, because no response is left to carry it. So after a pick the
 * screen polls the import, which re-reads the lists as its counts move, and
 * adds its `failures` to the ones refused up front: "picked six, got four"
 * stays visible (`architecture/0003`). A file already in the project,
 * unchanged, is counted as accounted for.
 *
 * **One import is followed at a time,** the latest pick's. A pick into the one
 * already followed, as every pick on setup's documents step is, adds to what it
 * waits for.
 *
 * **A joined import is waited for by its files, not its status.** The step
 * keeps it `open` until it is left, so the status would say "still arriving"
 * for as long as the step is up. It stops once every file this screen's picks
 * accepted has landed or been lost. That relies on every Drive file in the
 * import having come from this screen, which holds because the import is the
 * step's own.
 */
export function useDriveArrivals(projectId: string | undefined) {
  const [followed, setFollowed] = useState<Followed | null>(null);
  // The same query the screen lists from — an extra observer, not a fetch.
  const { data: documents } = useDocuments(projectId);

  // A file the project already holds, unchanged, is accepted and then not
  // stored again (`document-ai/0016` §11). It never lands and is not a
  // failure, so without `unchanged` it would be waited for for ever.
  const waitingFor = (current: ProjectImport): number =>
    followed
      ? Math.max(
          0,
          followed.expected -
            followed.landed.length -
            current.failures.length -
            current.unchanged,
        )
      : 0;

  const { data: current } = useImport(projectId, followed?.importId, {
    until: (current) => Boolean(followed?.joined) && waitingFor(current) === 0,
  });

  useEffect(() => {
    if (!followed) return;
    const fresh = (documents ?? []).filter(
      (document) =>
        document.importId === followed.importId &&
        document.source === "google-drive" &&
        !followed.landed.includes(document.id),
    );
    if (fresh.length === 0) return;
    setFollowed((before) => {
      if (before?.importId !== followed.importId) return before;
      const landed = new Set([...before.landed, ...fresh.map((document) => document.id)]);
      return { ...before, landed: [...landed] };
    });
  }, [documents, followed]);

  const follow = useCallback((result: DriveImportResult, { joined }: { joined: boolean }) => {
    setFollowed((before) =>
      before?.importId === result.import.id
        ? { ...before, expected: before.expected + result.accepted }
        : { importId: result.import.id, joined, expected: result.accepted, landed: [] },
    );
  }, []);

  return {
    follow,
    /** Picked files the worker could not fetch, and why. */
    failures: current?.failures ?? [],
    /** Files accepted and not yet landed, lost, or found already there. */
    arriving: current && importIsArriving(current) ? waitingFor(current) : 0,
  };
}

/** What the popup posts back to the opener. */
type CallbackMessage = { source: "gb-google-drive"; code?: string; error?: string; state?: string };

/**
 * Fetch the Google scripts before anybody clicks anything.
 *
 * Call it where a Drive button is rendered. Both loaders are idempotent and
 * cache their promise, so this is a no-op on the second call and on every
 * subsequent open — the point is that the *first* open does not spend its user
 * activation waiting for a script.
 *
 * Failure is deliberately swallowed. This is a warm-up: if it fails, the click
 * path loads the scripts itself and reports properly from there.
 */
export function preloadGoogleConnectors(): void {
  void loadGoogleApi().catch(() => {});
  void loadGoogleIdentity().catch(() => {});
}

/**
 * How long a closed popup is given to deliver the message it already posted.
 *
 * Generous on purpose: the cost of waiting is a beat before an honest
 * "cancelled", and the cost of not waiting is telling somebody their successful
 * sign-in failed.
 */
const CLOSED_GRACE_MS = 600;

/**
 * Open Google's consent screen and resolve with the returned code.
 *
 * The `message` listener checks `event.origin` against our own: the popup is on
 * our domain when it posts back, and without the check any page could post a
 * forged code into this window.
 */
function runConsentPopup(url: string): Promise<{ code: string; state: string }> {
  return new Promise((resolve, reject) => {
    const popup = window.open(url, "gb-google-drive", "width=520,height=640");
    if (!popup) {
      reject(new Error("Your browser blocked the Google sign-in window."));
      return;
    }

    const onMessage = (event: MessageEvent<CallbackMessage>): void => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.source !== "gb-google-drive") return;

      cleanup();
      if (event.data.error || !event.data.code || !event.data.state) {
        reject(new Error(event.data.error ?? "Google sign-in was cancelled."));
        return;
      }
      resolve({ code: event.data.code, state: event.data.state });
    };

    // A user who closes the popup gets a clear outcome rather than a promise
    // that never settles and a button stuck on "Connecting…".
    //
    // **But a closed popup is not the same as a cancelled sign-in**, and
    // treating it as one is a bug that only shows up on the happy path: the
    // callback page posts its code and then calls `window.close()`, and those
    // are two separate events. `popup.closed` can flip before the message has
    // been dispatched into this window, so a user who *accepted* consent was
    // told "Google sign-in was cancelled" — with a valid code sitting in the
    // queue behind the rejection.
    //
    // So a closed popup starts a grace period rather than settling. If the
    // message is in flight it arrives well inside it and `cleanup()` cancels
    // this; if the user really did close the window, nothing arrives and the
    // rejection stands a blink later than it used to.
    let giveUp: number | undefined;
    const poll = window.setInterval(() => {
      if (!popup.closed || giveUp !== undefined) return;
      giveUp = window.setTimeout(() => {
        cleanup();
        reject(new Error("Google sign-in was cancelled."));
      }, CLOSED_GRACE_MS);
    }, 250);

    function cleanup(): void {
      window.clearInterval(poll);
      if (giveUp !== undefined) window.clearTimeout(giveUp);
      window.removeEventListener("message", onMessage);
      if (!popup!.closed) popup!.close();
    }

    window.addEventListener("message", onMessage);
  });
}

// ------------------------------------------------------------------
// Picker
// ------------------------------------------------------------------

type PickedFile = {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes?: number;
  /** Its folders in Drive, when they could be looked up (2026-09-26, LIN-01). */
  sourcePath?: string;
  /** When it was last changed in Drive, from the picker's `lastEditedUtc` (2026-09-26, LIN-25). */
  modifiedAt?: string;
};

/** One Drive folder as `files.get` answers it: its name, and the folders it is in. */
export type DriveFolder = { name: string; parents?: string[] };

/**
 * How far up a file's folders are followed. Deeper than any project export
 * seen (5 in the German set), and a bound on the requests one file can cost.
 */
const MAX_DRIVE_FOLDER_DEPTH = 12;

/**
 * The folders of each picked file, from the folder it is in up to the drive's
 * own root, which is left out: "Projects/P1/Invoices" (2026-09-26, LIN-01).
 *
 * `lookup` answers one folder, or null when it could not. Each folder is asked
 * once, however many files are in it. A file whose chain could not be followed
 * gets no path: the pick never fails for want of one.
 */
export async function driveFolderPaths(
  parents: readonly (string | undefined)[],
  lookup: (folderId: string) => Promise<DriveFolder | null>,
): Promise<(string | null)[]> {
  const asked = new Map<string, Promise<DriveFolder | null>>();
  const folder = (id: string) => {
    if (!asked.has(id)) asked.set(id, lookup(id).catch(() => null));
    return asked.get(id)!;
  };
  return Promise.all(
    parents.map(async (parent) => {
      if (!parent) return null;
      const names: string[] = [];
      let id: string | undefined = parent;
      for (let depth = 0; id && depth < MAX_DRIVE_FOLDER_DEPTH; depth += 1) {
        const found = await folder(id);
        if (!found) return null;
        // A folder with no parent is the drive's root, "My Drive": not a folder
        // anybody filed into.
        if (!found.parents || found.parents.length === 0) break;
        names.unshift(found.name);
        id = found.parents[0];
      }
      return normaliseSourcePath(names.join("/"));
    }),
  );
}

/**
 * The folders of one pick, from the folder the person picked in and down
 * (2026-09-26, HID-13): the folders above it are left off, as a drop leaves
 * off the folders above the folder dropped.
 *
 * A Drive path runs up to "My Drive", so it held the folders a person keeps
 * their own work in, such as "Mirek - Slovinsko" above a project folder. The
 * folder picked in is the deepest one that holds every picked file. It is
 * kept, so a file picked alone keeps the folder it was in, and every folder
 * below it stays. A pick whose files share no folder is left as it is.
 */
export function fromPickedFolder(paths: readonly (string | null)[]): (string | null)[] {
  const split = paths.filter((path): path is string => path !== null).map((path) => path.split("/"));
  if (split.length === 0) return [...paths];
  let shared = split[0]!.length;
  for (const path of split) {
    let same = 0;
    while (same < shared && same < path.length && path[same] === split[0]![same]) same += 1;
    shared = same;
  }
  // The folder picked in is the last one they share; the ones above it go.
  const drop = Math.max(0, shared - 1);
  return paths.map((path) => (path === null ? null : path.split("/").slice(drop).join("/")));
}

/**
 * One Drive folder, read with the picker's own token. The token is the
 * browser's, for the picker's session only (see `requestPickerToken`).
 */
function driveLookup(accessToken: string): (folderId: string) => Promise<DriveFolder | null> {
  return async (folderId) => {
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(folderId)}?fields=name,parents&supportsAllDrives=true`,
      { headers: { authorization: `Bearer ${accessToken}` } },
    );
    if (!response.ok) return null;
    const body = (await response.json()) as { name?: unknown; parents?: unknown };
    if (typeof body.name !== "string") return null;
    const parents = Array.isArray(body.parents)
      ? body.parents.filter((value): value is string => typeof value === "string")
      : [];
    return { name: body.name, parents };
  };
}

/**
 * When a picked file was last changed in Drive, as an ISO time, or null when
 * the Picker gave none (LIN-25). The API checks it again (`normaliseFileTime`).
 */
export function editedAt(lastEditedUtc: number | undefined): string | null {
  return typeof lastEditedUtc === "number" && Number.isFinite(lastEditedUtc) && lastEditedUtc > 0
    ? new Date(lastEditedUtc).toISOString()
    : null;
}

/** The picked files with their folders, or as they were when the folders could not be read. */
async function withDriveFolders(
  docs: PickedDoc[],
  accessToken: string,
): Promise<PickedFile[]> {
  const files = docs.map((doc) => ({
    id: doc.id,
    name: doc.name,
    mimeType: doc.mimeType,
    sizeBytes: doc.sizeBytes ? Number(doc.sizeBytes) : undefined,
    ...(editedAt(doc.lastEditedUtc) ? { modifiedAt: editedAt(doc.lastEditedUtc)! } : {}),
  }));
  try {
    const paths = fromPickedFolder(
      await driveFolderPaths(
        docs.map((doc) => doc.parentId),
        driveLookup(accessToken),
      ),
    );
    return files.map((file, index) => (paths[index] ? { ...file, sourcePath: paths[index]! } : file));
  } catch (error) {
    console.warn("Couldn’t read the folders of the picked files. They are imported without them.", error);
    return files;
  }
}

/** MIME types the Picker offers, matching what the API will accept. */
const PICKER_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png",
  "image/jpeg",
  "text/plain",
  "text/markdown",
  "text/csv",
  // Google-native Docs and Sheets, which the API exports to PDF/XLSX.
  "application/vnd.google-apps.document",
  "application/vnd.google-apps.spreadsheet",
].join(",");

/**
 * Open the Google Picker.
 *
 * The Picker is a browser widget and needs an OAuth token *in the browser* —
 * but no endpoint of ours returns one, by design. So the browser obtains its
 * own short-lived token straight from Google Identity Services, for the same
 * `drive.file` scope, and never sees the refresh token the API holds for
 * downloading. Two independent grants for the same scope, which is precisely
 * how Google documents the Picker alongside a server-side code flow.
 *
 * Both scripts are injected rather than bundled: they are Google's, versioned
 * by them, and only work when served from their origin.
 */
/**
 * The Cloud project number, read off the OAuth client ID.
 *
 * Google issues client IDs as `<project-number>-<hash>.apps.googleusercontent.com`,
 * so the app id the Picker needs is already in a value the server sends us.
 * Deriving it beats adding a fifth connector variable that could only ever be
 * set to one thing and would be silently wrong if it were set to another.
 */
export function appIdFrom(clientId: string): string {
  return clientId.split("-")[0] ?? "";
}

/**
 * How long the Picker gets to *load* before we call it broken.
 *
 * This bounds initialisation only — `LOADED` cancels it, after which the user
 * can browse Drive for as long as they like. It exists because the Picker's
 * worst failure is silent: when the developer key is rejected, Google renders
 * its own "The API developer key is invalid" dialog inside its iframe and fires
 * no action at all. The promise never settled, so the `await` upstream hung
 * forever, its `catch` never ran, and nothing reached the console, the network
 * tab or our logs. The only artefact was Google's dialog — which is exactly why
 * a QA report of this arrives with no error output to work from.
 */
const PICKER_LOAD_TIMEOUT_MS = 30_000;

export async function openGooglePicker(options: {
  apiKey: string;
  clientId: string;
}): Promise<PickedFile[]> {
  // **The token first, and nothing slow before it.**
  //
  // Requesting it opens a popup, and Chrome only allows that while the click
  // that started this is still "transient user activation" — a few seconds.
  // This used to `await loadGoogleApi()` first: two script loads over the
  // network, which is long enough to lose the activation. The popup was then
  // blocked, no token was granted, and the picker was handed a stale one and
  // answered **401** — which Google's own dialog reports as
  // "The API developer key is invalid". The key was never involved.
  //
  // `preloadGoogleConnectors()` is what makes this reliable rather than merely
  // better: with both scripts already in the page there is no network hop
  // between the click and the popup at all.
  const accessToken = await requestPickerToken(options.clientId);
  await loadGoogleApi();

  return new Promise((resolve, reject) => {
    const google = (window as unknown as { google: GooglePickerNamespace }).google;

    let settled = false;
    const finish = (fn: () => void): void => {
      if (settled) return;
      settled = true;
      clearTimeout(loadTimer);
      fn();
    };

    // **This is a timeout, not a diagnosis.** It used to name three specific
    // causes as fact; all three were verified correct on an environment that
    // still failed (2026-08-05), so a confident message sent the reader to
    // check things that were already right. Google's own dialog carries the
    // actual reason and this one now says so.
    const loadTimer = setTimeout(() => {
      finish(() =>
        reject(
          new Error(
            "Google's file picker did not load within 30 seconds. If Google showed " +
              "its own dialog, that message is the real one — read it rather than " +
              "this. Common causes are the Picker API not being enabled on the Cloud " +
              "project, or the API key's restrictions excluding picker.googleapis.com " +
              "or this origin.",
          ),
        ),
      );
    }, PICKER_LOAD_TIMEOUT_MS);

    const view = new google.picker.DocsView(google.picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setMimeTypes(PICKER_MIME_TYPES);

    const picker = new google.picker.PickerBuilder()
      .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      .setDeveloperKey(options.apiKey)
      // Stated rather than inferred. Left to itself the Picker derives its
      // origin from the embedding page, and here it derived
      // `http://localhost:5173/favicon.ico`. That turned out **not** to be what
      // was breaking the picker — the scope was — and this is kept only because
      // an explicit origin is one fewer thing for Google to guess.
      .setOrigin(`${window.location.protocol}//${window.location.host}`)
      // Required by `drive.file`, and only by it. That scope grants access to
      // files the user picks *and nothing else*, so Google has to know which
      // application the grant is being made to — the app id is what ties the
      // picked file to our Cloud project. Omit it and the Picker still opens,
      // the user still picks, and the server-side fetch then 404s on a file
      // that visibly exists: the failure lands one step away from its cause.
      .setAppId(appIdFrom(options.clientId))
      .setOAuthToken(accessToken)
      .addView(view)
      .setCallback((data: PickerCallbackData) => {
        if (data.action === google.picker.Action.PICKED) {
          // With their folders, looked up with the picker's token before it
          // goes (2026-09-26, LIN-01). A lookup that fails costs the folder,
          // never the file.
          finish(() => void withDriveFolders(data.docs ?? [], accessToken).then(resolve));
        } else if (data.action === google.picker.Action.CANCEL) {
          finish(() => resolve([]));
        } else if (data.action === google.picker.Action.ERROR) {
          finish(() =>
            reject(new Error(`Google's file picker reported an error: ${describe(data)}`)),
          );
        } else if (data.action === google.picker.Action.LOADED) {
          // Loaded is the only positive signal Google gives us. Once it lands
          // the user may take as long as they like, so the timer goes.
          clearTimeout(loadTimer);
        } else {
          // Never silently ignored. An action we do not handle is the shape the
          // developer-key failure took, and swallowing it is what left the
          // promise pending with nothing written down anywhere.
          console.warn("Unhandled Google Picker action:", data.action, data);
        }
      })
      .build();

    picker.setVisible(true);
  });
}

/** Whatever Google put in the callback, in something loggable. */
function describe(data: PickerCallbackData): string {
  try {
    return JSON.stringify(data);
  } catch {
    return String(data.action);
  }
}

/**
 * A short-lived browser token for the Picker, from Google Identity Services.
 *
 * Scoped to `drive.readonly`, which is **broader than the server's stored
 * `drive.file` grant** — see the note on `scope` below for why the Picker
 * cannot browse without it, and why widening it here is not the same as
 * widening what this app keeps.
 */
function requestPickerToken(clientId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    loadGoogleIdentity()
      .then(() => {
        const accounts = (window as unknown as { google: GoogleIdentityNamespace })
          .google.accounts;
        const tokenClient = accounts.oauth2.initTokenClient({
          client_id: clientId,
          // **Both scopes, and only for this browser-held token.**
          //
          // `drive.readonly` is what lets the Picker *browse*. `drive.file`
          // cannot list a Drive, so with it alone the gadget request is refused
          // with 401, which Google's dialog reports as "The API developer key
          // is invalid". Measured 2026-08-05: the granted scope was
          // `…/auth/drive.file` and the picker 401'd with a completely
          // unrestricted API key.
          //
          // `drive.file` has to be here *as well*, and leaving it out is what
          // broke importing. Picking a file through a Picker that carries this
          // app's id records a per-file grant — but only against a `drive.file`
          // grant, and only if the picker token actually holds that scope. With
          // a readonly-only token the browsing worked and nothing was ever
          // granted, so the server's stored `drive.file` credential could not
          // download the very file the user had just chosen.
          //
          // The **stored** server-side credential stays `drive.file` alone
          // (`google-drive.service.ts`) and that is the one that matters for
          // privacy: it is what this app keeps and can use later. This token is
          // minted in the browser for the lifetime of one picker session, never
          // persisted, and never leaves the page.
          scope:
            "https://www.googleapis.com/auth/drive.readonly " +
            "https://www.googleapis.com/auth/drive.file",
          callback: (response) => {
            if (response.access_token) resolve(response.access_token);
            else reject(new Error("Google didn’t grant access to the Picker."));
          },
          error_callback: () => reject(new Error("Google sign-in was cancelled.")),
        });
        tokenClient.requestAccessToken();
      })
      .catch(reject);
  });
}

let googleApiPromise: Promise<void> | null = null;
let googleIdentityPromise: Promise<void> | null = null;

function loadGoogleApi(): Promise<void> {
  googleApiPromise ??= injectScript("https://apis.google.com/js/api.js")
    .then(
      () =>
        new Promise<void>((resolve) => {
          const gapi = (
            window as unknown as { gapi: { load: (m: string, cb: () => void) => void } }
          ).gapi;
          gapi.load("picker", () => resolve());
        }),
    )
    .catch((error: unknown) => {
      // Reset, so a later attempt retries rather than being stuck on a
      // permanently rejected promise.
      googleApiPromise = null;
      throw error;
    });
  return googleApiPromise;
}

function loadGoogleIdentity(): Promise<void> {
  googleIdentityPromise ??= injectScript("https://accounts.google.com/gsi/client").catch(
    (error: unknown) => {
      googleIdentityPromise = null;
      throw error;
    },
  );
  return googleIdentityPromise;
}

function injectScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Couldn’t reach Google."));
    document.head.appendChild(script);
  });
}

type GoogleIdentityNamespace = {
  accounts: {
    oauth2: {
      initTokenClient: (config: {
        client_id: string;
        scope: string;
        callback: (response: { access_token?: string }) => void;
        error_callback: () => void;
      }) => { requestAccessToken: () => void };
    };
  };
};

/** One picked file as the Picker describes it. `lastEditedUtc` is milliseconds since 1970. */
type PickedDoc = {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes?: string;
  parentId?: string;
  lastEditedUtc?: number;
};

type PickerCallbackData = {
  action: string;
  docs?: PickedDoc[];
};

type GooglePickerNamespace = {
  picker: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    DocsView: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    PickerBuilder: any;
    ViewId: { DOCS: string };
    Feature: { MULTISELECT_ENABLED: string };
    Action: { PICKED: string; CANCEL: string; LOADED?: string; ERROR?: string };
  };
};
